import { useState, useEffect, lazy, Suspense } from "react";
import { db } from "./firebase";
import { getDoc } from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { tdoc } from "./utils/firebase";
import { setActiveCompany, resetActiveCompany, getActiveCompany, AUTH_ENABLED, EMAIL_AUTH, setActiveFeatures, resetFeatures, hasFeature, setBaseCurrency, resetBaseCurrency, setStoreName, resetStoreName, setActivePlan, resetPlan, setBusinessType, resetBusinessType } from "./utils/tenant";
import { saveSession, loadSession, clearSession } from "./utils/session";
import { addLog } from "./utils/logs";
import { setMonitoringUser, clearMonitoringUser } from "./utils/monitoring";
import { registerPush, unregisterPush } from "./utils/notifications";
import { readLoaderPrefs, loaderLogo, loaderDotsCss } from "./utils/loaderPrefs";
import { applyCachedStoreFonts } from "./utils/storeFonts";
// ── Shared shell components (تبقى static — صغيرة وتُستخدم في القشرة الأساسية) ───
import Spinner from "./components/Spinner";
import OfflineBanner from "./components/OfflineBanner";
import ErrorBoundary from "./components/ErrorBoundary";
// lazy مع مرونة النشر (deploy-safe): عند فشل تحميل شنك — غالباً لأن نشراً جديداً
// غيّر أسماء ملفات الشنكات فصار اسم الشنك القديم غير موجود (404) — نعيد المحاولة
// مرة، وإن فشلت نعمل reload تلقائياً مرة واحدة فقط (حارس sessionStorage يمنع حلقة
// إعادة تحميل) فيُجلب index.html الجديد بأسماء الشنكات الصحيحة، فتختفي نافذة النشر
// عن الزائر. لو فشل حتى بعد إعادة التحميل، نرمي الخطأ لِـ ErrorBoundary (شبكة أمان).
const _CHUNK_RELOAD_KEY = "bkv_chunk_reload_once";
function lazyWithReload(factory) {
  return lazy(() =>
    factory()
      .catch(() => new Promise((res) => setTimeout(res, 500)).then(factory)) // إعادة محاولة واحدة
      .then((mod) => { try { sessionStorage.removeItem(_CHUNK_RELOAD_KEY); } catch {} return mod; })
      .catch((err) => {
        try {
          if (!sessionStorage.getItem(_CHUNK_RELOAD_KEY)) {
            sessionStorage.setItem(_CHUNK_RELOAD_KEY, "1");
            window.location.reload();
            return new Promise(() => {}); // لا يُحلّ أبداً — نتجنّب وميض خطأ قبل reload
          }
        } catch {}
        throw err;
      })
  );
}

// ── الشاشات الرئيسية: lazy عبر React.lazy، فكل سياق يحمّل كوده فقط ───────────────
// زائر المتجر لا يحمّل كود الأدمن (الطلبات/الواتساب/المحاسبة…)، والأدمن لا يحمّل
// كود المتجر (365KB). كان كل شي مجمّعاً في باندل واحد (~843KB) يُنزَّل في كل ريفرش
// حتى لو الزائر ما يحتاجه — هذا التقسيم يقلّص حجم JS في أول تحميل بشكل كبير.
const TailorApp     = lazyWithReload(() => import("./features/tailor/TailorApp"));
const PosApp        = lazyWithReload(() => import("./features/pos/PosApp"));
const PackerApp     = lazyWithReload(() => import("./features/packer/PackerApp"));
const EmployeeApp   = lazyWithReload(() => import("./features/employee/EmployeeApp"));
const PrivacyPolicy = lazyWithReload(() => import("./features/store/PrivacyPolicy"));
const Storefront    = lazyWithReload(() => import("./features/ecommerce/Storefront"));
const LoginScreen   = lazyWithReload(() => import("./features/company/LoginScreen"));
const CompanyPanel  = lazyWithReload(() => import("./features/company/CompanyPanel"));
const SuperAdminApp = lazyWithReload(() => import("./features/company/SuperAdminApp"));
// شاشة العودة من صفحة الدفع — لا تُحمَّل إلا إذا حمل الرابط ?checkout=…
const CheckoutReturn = lazyWithReload(() => import("./features/company/CheckoutReturn"));
// صفحة الموافقة على ربط ChatGPT (OAuth) — لا تُحمَّل إلا على المسار /oauth/authorize.
const McpConsent = lazyWithReload(() => import("./features/company/McpConsent"));


// ── ROOT ──────────────────────────────────────────────────────────────────────

// دومينات المنصّة (لوحة الإدارة). أي هوست منها = ليس متجر عميل (يُعرض الأدمن لا المتجر).
// نقبل الدومين القديم my.bekvamkw.com والجديد my.velorci.com معاً أثناء الانتقال.
const PLATFORM_HOSTS = ["my.bekvamkw.com", "my.velorci.com"];
function isPlatformHost(h) {
  return PLATFORM_HOSTS.includes(h) || h.endsWith(".vercel.app") || h === "localhost" || h === "127.0.0.1";
}

// سياق المتجر العام (Storefront): يُحسب مرة عند تحميل الصفحة من المسار/الدومين.
// في هذا السياق يجب ألا يلمس App الشركة النشطة إطلاقاً (StorefrontGate هو المرجع).
const _IS_STORE_CTX = (() => {
  const p = window.location.pathname;
  const h = window.location.hostname;
  return p.startsWith("/store")
    || /^\/co?\/[A-Za-z0-9]/.test(p)
    || !isPlatformHost(h);
})();

// نسخة خفيفة من استخراج companyId من مسار /store/<seg> (مطابقة لـ STORE_PATH_CID
// في Storefront.js). نحسبها هنا محلياً كي لا نستورد Storefront.js الضخم بشكل ثابت —
// فيبقى قابلاً للتحميل الكسول (lazy) ولا يدخل الباندل الرئيسي.
function storePathCid() {
  const h = window.location.hostname;
  const isCustomDomain = !isPlatformHost(h);
  if (isCustomDomain) return null;
  const segs = window.location.pathname.split("/").filter(Boolean); // ["store", seg?, …]
  if (segs[0] !== "store" || !segs[1]) return null;
  const RESERVED = new Set(["product", "products", "category", "cart", "checkout", "success", "page"]);
  if (RESERVED.has(segs[1])) return null;
  return decodeURIComponent(segs[1].split("--").pop());
}

// خطوط المتجر المختارة (عربي/إنجليزي) — تُطبَّق من الكاش المحلي فور تحميل الملف،
// أي قبل أول رسم وقبل وصول الإعدادات من Firestore، فلا يحدث وميض تبديل خط.
// في سياق لوحة الإدارة لا يُنفَّذ شيء إطلاقاً.
if (_IS_STORE_CTX) { try { applyCachedStoreFonts(storePathCid()); } catch {} }

// لودر محايد للمتجر العام (سياق الزبون): يعرض اسم المتجر من الكاش على خلفية بيضاء —
// ولا يعرض شعار المنصة (velorci) للزبون. مطابق للـ Spinner المحلي داخل Storefront
// فلا يحدث قفز بصري بين بوابة المتجر وتحميل بيانات المتجر. لو ما في اسم مخزّن
// (أول زيارة) يظهر مؤشّر محايد فقط. لا نستخدم شعار velorci هنا إطلاقاً.
function StoreLoader() {
  const host = window.location.hostname;
  const lang = localStorage.getItem("bkv_lang") || "ar";
  const nameAr = localStorage.getItem(`bkv_sname_ar_${host}`) || "";
  const nameEn = localStorage.getItem(`bkv_sname_en_${host}`) || "";
  const full = lang === "en" ? (nameEn || nameAr) : (nameAr || nameEn);
  // ألوان الشاشة وحركة الاسم/اللوجو من تفضيلات التاجر (مخزّنة محلياً من الزيارة السابقة)
  const prefs = readLoaderPrefs(storePathCid());
  const { bg, color, anim } = prefs;
  const logoSrc = loaderLogo(prefs, lang);
  const logoMode = anim === "logo";
  const [txt, setTxt] = useState(anim === "none" ? full : "");
  useEffect(() => {
    if (!full || logoMode) return;
    if (anim === "none") { setTxt(full); return; }
    let i = 0, del = false, timer;
    const tick = () => {
      if (!del) {
        i++; setTxt(full.slice(0, i));
        if (i === full.length) {
          if (anim === "write") return;   // «يُكتب فقط» — يتوقف عند اكتمال الاسم
          del = true; timer = setTimeout(tick, 900); return;
        }
      } else {
        i--; setTxt(full.slice(0, i));
        if (i === 0) { del = false; timer = setTimeout(tick, 400); return; }
      }
      timer = setTimeout(tick, del ? 70 : 110);
    };
    timer = setTimeout(tick, 300);
    return () => clearTimeout(timer);
  }, [full, anim, logoMode]);
  // وضع اللوجو: لوجو الهيدر + ثلاث نقاط تحميل (نفس حركة لوحة التحكم)
  if (logoMode) return (
    <div style={{ position: "fixed", inset: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {logoSrc
          ? <img src={logoSrc} alt="" decoding="async" style={{ height: prefs.logoSize, maxWidth: "70vw", objectFit: "contain" }} />
          : <div style={{ fontSize: 32, fontWeight: 900, color, fontFamily: "var(--stf, Cairo, sans-serif)", letterSpacing: 2, textAlign: "center" }}>{full}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: prefs.dotsGap }}>
          <span className="ldr-dot" /><span className="ldr-dot" /><span className="ldr-dot" />
        </div>
      </div>
      <style>{loaderDotsCss(color)}</style>
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ fontSize: 42, fontWeight: 900, color, fontFamily: "var(--stf, Cairo, sans-serif)", minWidth: 220, letterSpacing: 2, textAlign: "center" }}>
        {txt}{anim !== "none" && anim !== "logo" && <span style={{ color, animation: "blink 0.65s step-end infinite" }}>|</span>}
      </div>
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </div>
  );
}

// صفحة "غير موجودة" — تُعرض لروابط الديمو (my.bekvamkw.com/store/…) بعد أن تُلغيها
// الشركة من إعداداتها؛ فلا يفتح المتجر إلا عبر دومين الشركة الحقيقي.
function StoreNotFound() {
  return (
    <div dir="rtl" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, background:"#f8fafc", fontFamily:"var(--stf, Cairo, sans-serif)", padding:24, textAlign:"center" }}>
      <div style={{ fontSize:56 }}>🔍</div>
      <div style={{ fontSize:20, fontWeight:800, color:"#334155" }}>الصفحة غير موجودة</div>
      <div style={{ fontSize:13, color:"#64748b", maxWidth:340, lineHeight:1.7 }}>هذا الرابط لم يعد متاحاً.</div>
    </div>
  );
}

// تُعرض حين يتعذّر تحديد شركة الدومين: سقط مسارا الحلّ معاً (قراءة Firestore من
// المتصفح، ثم سؤال سيرفرنا). الأهم ما **لا** تفعله: لا تعرض متجر شركة أخرى.
// عرض متجر خاطئ على دومين تاجر ليس خطأ عرض — الطلب يُكتب في حساب الشركة الأخرى.
// وهي شاشة تُصلح نفسها: لو وصل جواب Firestore متأخراً أُعيد التحميل تلقائياً.
function StoreUnresolved() {
  return (
    <div dir="rtl" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, background:"#f8fafc", fontFamily:"var(--stf, Cairo, sans-serif)", padding:24, textAlign:"center" }}>
      <div style={{ fontSize:56 }}>📡</div>
      <div style={{ fontSize:20, fontWeight:800, color:"#334155" }}>تعذّر تحميل المتجر</div>
      <div style={{ fontSize:13, color:"#64748b", maxWidth:340, lineHeight:1.7 }}>يبدو أن الاتصال ضعيف. جرّب إعادة المحاولة.</div>
      <button onClick={() => window.location.reload()} style={{ marginTop:6, background:"#334155", color:"#fff", border:"none", borderRadius:10, padding:"11px 26px", fontWeight:700, cursor:"pointer", fontFamily:"var(--stf, Cairo, sans-serif)", fontSize:14 }}>إعادة المحاولة</button>
    </div>
  );
}

// ── المسار الاحتياطي لحلّ شركة الدومين ────────────────────────────────────────
//
// يسأل سيرفرنا عبر طلب HTTPS عادي — **نقل مختلف** عن long-poll المفروض في
// src/firebase.js، وهو ما يتعلّق على شبكات iOS/الجوال المتذبذبة. فينجح عادةً
// حين يتعلّق ذاك، ويظلّ الجواب من نفس مصدر الحقيقة (domains/{host}) لا تخميناً.
//
// ⚠️ لا نحتفظ بنسخة محلية من شركة الدومين عمداً، رغم أنها تبدو احتياطياً مغرياً:
//   • لا تشتري شيئاً: لو سقط النقلان معاً فلن يحمّل المتجر أصلاً — اشتراكاته على
//     نفس نقل Firestore — فينتهي الزائر إلى شاشة تعذّر التحميل داخل Storefront.
//   • وتحمل خطراً حقيقياً: دومين أُعيد ربطه لشركة أخرى يبقى في المتصفح محفوظاً
//     بشركته القديمة، فتُعرض تلك الشركة ويُكتب الطلب في حسابها.
// أي أنها كل المخاطرة بلا فائدة تُذكر. الصواب: لا يُعرض متجر بشركة غير محسومة.
//
// يرجّع:  { cid }  جواب حاسم — "" يعني دومين غير مربوط · null  تعذّر الحسم.
async function resolveCidFromServer() {
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 6000);
    const r = await fetch("/api/invoice-proxy?action=resolve-domain", { signal: ctl.signal });
    clearTimeout(t);
    if (!r.ok) return null;
    const d = await r.json();
    return d && d.found ? { cid: String(d.companyId || "") } : null;
  } catch { return null; }
}

// يحلّ الشركة من الدومين قبل عرض المتجر العام، فيحمّل متجر الشركة الصحيحة.
// الدومين غير المربوط (أو my.bekvamkw.com/preview) → الشركة الافتراضية (متوافق رجعياً).
function StorefrontGate() {
  const [ready, setReady] = useState(false);
  const [demoBlocked, setDemoBlocked] = useState(false);
  const [unresolved, setUnresolved] = useState(false);
  useEffect(() => {
    const host = window.location.hostname.replace(/^www\./, "");
    if (isPlatformHost(host)) {
      // روابط المعاينة على نطاق المنصّة لا تُفهرَس: المتجر يُفهرَس على دومين التاجر
      // وحده. يمنع تكرار المحتوى بين الرابطين، ويمنع ظهور متاجر جديدة/غير مكتملة
      // (والتسجيل الذاتي مفتوح للعموم) في نتائج البحث تحت نطاق المنصّة.
      try {
        let m = document.head.querySelector('meta[name="robots"]');
        if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
        m.setAttribute("content", "noindex,nofollow");
      } catch {}
      // وضع المعاينة (الديمو): my.bekvamkw.com/store/<companyId> يعرض متجر تلك الشركة.
      // لكن لو الشركة ألغت روابط الديمو من إعداداتها (ecommerce_config.demoDisabled)
      // نقفل المعاينة نهائياً (صفحة غير موجودة) فلا يفتح المتجر إلا عبر دومينها الحقيقي.
      const cid = storePathCid();
      const targetCid = cid || getActiveCompany();
      // هنا الشركة معروفة **من المسار** قبل أي شبكة، والقراءة لفحص demoDisabled
      // وحده. فحارس التعليق يكمل بالشركة الصحيحة بلا أي تخمين. (بلا حارس كانت
      // قراءة معلّقة — لا تنجح ولا تفشل — تترك شاشة التحميل للأبد.)
      let settled = false;
      const finish = (fn) => { if (settled) return; settled = true; fn(); };
      const tmr = setTimeout(() => finish(() => { if (cid) setActiveCompany(cid); setReady(true); }), 8000);
      import("firebase/firestore")
        .then(({ getDoc, doc }) => getDoc(doc(db, "companies", targetCid, "bekvam", "ecommerce_config")))
        .then(s => finish(() => {
          clearTimeout(tmr);
          if (s.exists() && s.data()?.demoDisabled) { setDemoBlocked(true); setReady(true); return; }
          if (cid) setActiveCompany(cid);
          setReady(true);
        }))
        .catch(() => finish(() => { clearTimeout(tmr); if (cid) setActiveCompany(cid); setReady(true); }));
      return () => clearTimeout(tmr);
    }

    // ── دومين تاجر: الشركة تُحلّ من domains/{host} ─────────────────────────────
    //
    // ⚠️ الحارس الزمني هنا كان يكمل بـ setReady(true) وحدها، أي بالشركة الافتراضية
    // "01" ما دامت القراءة لم تعد بعد. وهذا لم يكن خطأ عرض عابراً: اشتراكات المتجر
    // في Storefront.js تعمل مرّة واحدة بـ useEffect(...,[]) وتقرأ getActiveCompany()
    // **لحظة التركيب**، فتلتصق بمسار شركة 01 لبقية الجلسة ⇒ الزائر يرى منتجات
    // وأسعار شركة أخرى على دومين هذا التاجر، وإن أكمل الطلب كُتب في حساب تلك
    // الشركة. وفرض experimentalForceLongPolling يجعل بلوغ الثماني ثوانٍ واقعياً
    // على شبكة جوال متذبذبة.
    //
    // القاعدة الآن: **لا يُعرض المتجر أبداً بشركة لم تُحسم لهذا الدومين.** وعند
    // تأخّر قراءة Firestore أو فشلها نسأل سيرفرنا (resolve-domain) — نفس مصدر
    // الحقيقة عبر نقل آخر — وإن تعذّر الحسم هناك أيضاً نعرض شاشة إعادة محاولة.
    // لا تخمين ولا نسخة محفوظة: أي منهما قد يعني متجر شركة أخرى على هذا الدومين.
    let committed = "";     // الشركة التي عُرض بها المتجر فعلاً
    let settled = false;
    const commit = (cid) => {
      if (settled) return;
      settled = true;
      committed = String(cid);
      setActiveCompany(committed);
      setReady(true);
    };
    const giveUp = () => { if (settled) return; settled = true; setUnresolved(true); };
    // يُنادى حين تعذّرت قراءة Firestore (تأخّر أو خطأ) — لا في المسار السعيد.
    const fallback = async () => {
      if (settled) return;
      const srv = await resolveCidFromServer();
      if (settled) return;
      // srv.cid فارغة = السيرفر قرأ الوثيقة ولم يجدها ⇒ دومين غير مربوط ⇒ الشركة
      // الافتراضية، وهو السلوك الموثّق في CLAUDE.md. srv نفسها null = لم يُحسم.
      if (srv) commit(srv.cid || getActiveCompany()); else giveUp();
    };

    const tmr = setTimeout(fallback, 8000);

    import("firebase/firestore")
      .then(({ getDoc, doc }) => getDoc(doc(db, "domains", host)))
      .then(s => {
        clearTimeout(tmr);
        const fresh = s.exists() && s.data()?.companyId ? String(s.data().companyId) : "";
        if (!settled) {
          // دومين غير مربوط (الوثيقة غير موجودة) → الشركة الافتراضية، كما كان
          // تماماً وكما هو موثّق في CLAUDE.md. هذا جوابٌ حاسم من السيرفر لا تخمين.
          commit(fresh || getActiveCompany());
          return;
        }
        // وصل الجواب متأخراً بعد أن حُسم الأمر. لو خالف ما عُرض فعلاً — وأهمّ
        // حالاته أن تكون شاشة «تعذّر التحميل» ظاهرة (committed فارغ) ثم وصل
        // الجواب — نعيد التحميل فيتعافى الزائر تلقائياً بلا أن ينتظر ضغطة.
        if (fresh && fresh !== committed) window.location.reload();
      })
      .catch(() => { clearTimeout(tmr); fallback(); });
    return () => clearTimeout(tmr);
  }, []);
  if (demoBlocked) return <StoreNotFound />;
  if (unresolved) return <StoreUnresolved />;
  if (!ready) return <StoreLoader />;
  return <Suspense fallback={<StoreLoader />}><Storefront /></Suspense>;
}

// تُعرض لموظف (خياط/تعبئة) عندما تكون الميزة معطّلة لشركته — بدل شاشة فارغة.
function FeatureDisabled({ onLogout }) {
  return (
    <div dir="rtl" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, background:"#f8fafc", fontFamily:"Cairo,sans-serif", padding:24, textAlign:"center" }}>
      <div style={{ fontSize:48 }}>🔒</div>
      <div style={{ fontSize:16, fontWeight:800, color:"#334155" }}>هذه الميزة غير مفعّلة لحسابك</div>
      <div style={{ fontSize:13, color:"#64748b", maxWidth:320 }}>يرجى التواصل مع مزوّد الخدمة لتفعيل هذه الميزة.</div>
      <button onClick={onLogout} style={{ background:"#334155", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontWeight:700, cursor:"pointer", fontFamily:"Cairo,sans-serif", fontSize:13 }}>🚪 خروج</button>
    </div>
  );
}

export default function App() {
  const [user,setUser]=useState(()=>{
    const u=loadSession();
    // عزل البيانات: يضبط الشركة النشطة من الجلسة. في سياق المتجر لا نلمسها إطلاقاً
    // (StorefrontGate يحلّها من الدومين/المسار) كي لا تتسرّب شركة جلسة الأدمن المسجّل.
    if(!_IS_STORE_CTX) setActiveCompany(u?.companyId);
    setMonitoringUser(u);             // وسم أخطاء Sentry بالشركة/الدور عند استعادة الجلسة
    return u;
  });

  // إعادة قراءة الدور/الصلاحيات من Firebase claims عند كل تحميل للصفحة (refresh).
  // المشكلة: الصلاحيات تُحفظ في الجلسة (localStorage) وقت الدخول فقط، فلو عدّل
  // الأدمن صلاحيات الموظف تبقى القديمة لحد ما يسجّل خروج/دخول. هنا نجبر تحديث
  // التوكن (getIdTokenResult(true)) فنقرأ أحدث claims ونحدّث الجلسة فوراً —
  // فيكفي الموظف يعمل refresh لتظهر الصلاحية الجديدة.
  useEffect(()=>{
    if(_IS_STORE_CTX) return;        // المتجر العام: لا نلمس الجلسة/الشركة النشطة
    if(!AUTH_ENABLED && !EMAIL_AUTH) return;
    const unsub = onAuthStateChanged(auth, async (fbUser)=>{
      if(!fbUser) return;
      const prev = loadSession();
      if(!prev) return;
      let c;
      try { c = (await fbUser.getIdTokenResult(true)).claims || {}; } catch { return; }
      const next = (c.superAdmin && !c.companyId)
        ? { ...prev, role:"superadmin", superAdmin:true, name:c.name||prev.name||"" }
        : c.role === "company"
        ? { ...prev, role:"company", companyId:c.companyId, name:c.name||prev.name||"", superAdmin:!!c.superAdmin }
        : { ...prev, role:c.role||prev.role||"driver", permissions:c.permissions||[], companyId:c.companyId, name:c.name||prev.name||prev.id };
      if(JSON.stringify(next)===JSON.stringify(prev)) return;
      saveSession(next);
      setActiveCompany(next.companyId);
      setMonitoringUser(next);
      setUser(next);
    });
    return ()=>unsub();
  },[]);

  // تجديد تسجيل إشعارات الدفع (FCM) عند كل فتح للتطبيق وعند عودته للمقدّمة.
  // كان التسجيل يحدث وقت تسجيل الدخول فقط: مَن يبقى مسجّلاً (الحالة الطبيعية) لا
  // يُجدَّد توكنه أبداً، فإذا دوّره المتصفح/النظام أو حذفه السيرفر كتوكن منتهٍ
  // (registration-token-not-registered) لا يعود للقائمة إلا بخروج ودخول يدوي →
  // تتوقّف إشعارات الخلفية (واتساب/الطلبات) فجأة وبلا سبب ظاهر. registerPush
  // خفيفة: لا تطلب إذناً أبداً بدون تفاعل، وتكتب فقط عند تغيّر التوكن أو مرور 6 ساعات.
  useEffect(()=>{
    if(_IS_STORE_CTX) return;
    if(!user || user.role==="superadmin") return;
    const run = () => { if(document.visibilityState==="visible") registerPush(user); };
    run();
    document.addEventListener("visibilitychange", run);
    // تطبيق الجوال: عندما يدوّر FCM توكن الجهاز نعيد التسجيل فوراً بـ force،
    // وإلا منعته مهلة الست ساعات من الحفظ وتوقفت الإشعارات صامتةً طوال تلك المدة.
    const onTokenChanged = () => registerPush(user, {force:true});
    window.addEventListener("native-push-token-changed", onTokenChanged);
    return ()=>{
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("native-push-token-changed", onTokenChanged);
    };
  },[user]);

  // مميزات الشركة (feature flags): تُحمّل مرة عند الدخول/استعادة الجلسة من
  // companies/{cid}/meta/info.features. حتى تُحمّل لا نعرض واجهة الشركة (سبينر بسيط)
  // تفادياً لوميض إظهار/إخفاء الموديولات. السوبر-أدمن لا يحتاجها (لا يرى داتا شركة).
  const [featuresLoaded,setFeaturesLoaded]=useState(false);
  const [,setMetaTick]=useState(0);
  useEffect(()=>{
    if(!user || user.role==="superadmin"){ resetFeatures(); resetBaseCurrency(); resetStoreName(); resetPlan(); resetBusinessType(); setFeaturesLoaded(true); return; }
    let cancelled=false;
    // إقلاع بارد سريع: لما يرجع التاجر للتطبيق/الـ PWA بعد ما النظام قتل الصفحة بالخلفية،
    // كانت اللوحة تنتظر 3 قراءات Firestore متتالية (long-polling بطيء على الجوال) قبل أن
    // تظهر — فيبدو كأنه فتح التطبيق للتوّ. الآن نعرض فوراً آخر نسخة محفوظة لهذه الشركة
    // ونحدّثها بالخلفية. المحتوى غير حسّاس (مميزات/عملة/باقة/اسم متجر) والمفتاح مربوط
    // بـ companyId فلا تنتقل قيم شركة لأخرى على نفس الجهاز.
    const metaKey = "bkv_meta_" + (user.companyId || getActiveCompany() || "");
    const applyMeta = (m) => {
      setActiveFeatures(m?.features||null, m?.featureModes||null);
      setBaseCurrency(m?.baseCurrency||null);
      setBusinessType(m?.businessType||null);
      if (m) setActivePlan({ package:m.package||null, subscription:m.subscription||null }); else resetPlan();
    };
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(metaKey) || "null"); } catch {}
    if (cached) {
      applyMeta(cached.info || null);
      if (cached.storeName !== undefined) setStoreName(cached.storeName);
      setFeaturesLoaded(true);
    } else {
      setFeaturesLoaded(false);
    }
    const save = (patch) => {
      try {
        const cur = JSON.parse(localStorage.getItem(metaKey) || "{}") || {};
        localStorage.setItem(metaKey, JSON.stringify({ ...cur, ...patch }));
      } catch {}
    };
    // اسم المتجر العام لرابط المعاينة (/store/<اسم>--<رقم>) — الاسم الصحيح للشركة،
    // لا اسم المستخدم المسجّل (كان اسم الموظف يتسرّب للرابط سابقاً). نطابق ترتيب
    // أولوية المتجر العام: ecommerce_config.storeName يطغى على website_settings.storeName.
    // بالتوازي مع meta/info ولا يحجب ظهور اللوحة (يُستعمل لرابط المعاينة فقط).
    Promise.all([
      getDoc(tdoc("bekvam","ecommerce_config")),
      getDoc(tdoc("bekvam","website_settings")),
    ]).then(([cfg,ws])=>{
      if(cancelled) return;
      const nm=(cfg.exists()&&cfg.data().storeName)||(ws.exists()&&ws.data().storeName)||null;
      setStoreName(nm);
      save({ storeName: nm });
    }).catch(()=>{ if(!cancelled && !cached) setStoreName(null); });
    (async()=>{
      try{
        const snap=await getDoc(tdoc("meta","info"));
        if(cancelled) return;
        // features = مفعّلة/معطّلة · featureModes = وضع المعطّلة (إخفاء أم قفل ظاهر)
        // baseCurrency (فارغ→KWD) · businessType (تجارة/خدمات — للتسمية فقط)
        // package/subscription — تُعرض في قائمة الشركة تحت شعار velorci.
        const d = snap.exists() ? snap.data() : null;
        const info = d ? {
          features: d.features||null, featureModes: d.featureModes||null,
          baseCurrency: d.baseCurrency||null, businessType: d.businessType||null,
          package: d.package||null, subscription: d.subscription||null,
        } : null;
        const changed = JSON.stringify(info) !== JSON.stringify(cached?.info ?? null);
        applyMeta(info);
        save({ info });
        // القيم تُقرأ من وحدة tenant وقت الرندر — إن تغيّرت عن المحفوظ نعيد الرندر مرة.
        if (cached && changed) setMetaTick(n=>n+1);
      }catch{
        // عند الخطأ: إن كان عندنا نسخة محفوظة نبقيها؛ وإلا شركة قديمة (كل المميزات مفعّلة، KWD، تجارة)
        if(!cancelled && !cached){ setActiveFeatures(null); setBaseCurrency(null); resetPlan(); resetBusinessType(); }
      }
      if(!cancelled) setFeaturesLoaded(true);
    })();
    return ()=>{cancelled=true;};
  },[user]);

  const handleLogin=(u)=>{
    setActiveCompany(u?.companyId);
    setMonitoringUser(u);             // وسم أخطاء Sentry بالشركة/الدور عند الدخول
    setUser(u);
    addLog("تسجيل دخول", `${u.name||u.id||"شركة"} — ${u.role||"company"}`, u.name||u.id||"شركة", u.role||"company", {role:u.role||"company"});
    // الدخول = تفاعل مباشر من المستخدم، فهنا فقط يجوز طلب إذن الإشعارات (prompt).
    // التسجيل نفسه (بمفتاح جهاز مستقل + تنظيف توكن السوبر-أدمن) في utils/notifications.
    registerPush(u, {prompt:true, force:true});
  };
  const handleLogout=()=>{
    addLog("تسجيل خروج", user?.name||user?.id||"شركة", user?.name||user?.id||"شركة", user?.role||"company", {role:user?.role||"company"});
    if(user?.role==="company" || user?.role==="company_employee"){
      unregisterPush(user);   // يحذف تسجيل هذا الجهاز فقط، لا أجهزة المستخدم الأخرى
    }
    clearSession();
    resetActiveCompany();
    resetFeatures();
    resetPlan();                     // باقة/اشتراك الشركة السابقة لا يبقيان بعد الخروج (عزل)
    resetStoreName();                // امسح اسم متجر الشركة السابقة عند الخروج (عزل)
    clearMonitoringUser();           // فكّ ربط الشركة عن أخطاء Sentry اللاحقة
    if(AUTH_ENABLED){ try{ signOut(auth); }catch{} }
    setUser(null);
  };

  // العودة من صفحة دفع الاشتراك: ?checkout=success&session_id=… (أو cancel).
  // نقرأ المعاملات مرة واحدة عند التركيب ثم ننظّف الرابط، فلا يعيد الريفرش الشاشة.
  const [checkoutRet, setCheckoutRet] = useState(() => {
    if (_IS_STORE_CTX) return null;
    try {
      const p = new URLSearchParams(window.location.search);
      const st = p.get("checkout");
      if (st !== "success" && st !== "cancel") return null;
      const sid = p.get("session_id") || "";
      p.delete("checkout"); p.delete("session_id"); p.delete("inv");
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      return { status: st, sessionId: sid };
    } catch { return null; }
  });

  const _h = window.location.hostname;
  const _isStoreDomain = !isPlatformHost(_h);
  // ربط ChatGPT: دخول (إن لزم) ثم صفحة الموافقة. على دومين المنصّة فقط.
  if(!_isStoreDomain && window.location.pathname === "/oauth/authorize") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#E8FFFE" }}><Spinner /></div>}>
          {!user ? <LoginScreen onLogin={handleLogin} /> : <McpConsent onLogout={handleLogout} />}
        </Suspense>
      </ErrorBoundary>
    );
  }
  // صفحة سياسة الخصوصية — عامة على كل دومين (مطلوبة لتطبيق Meta)
  if(window.location.pathname === "/privacy" || window.location.pathname === "/store/privacy") {
    return <ErrorBoundary><Suspense fallback={<StoreLoader />}><PrivacyPolicy /></Suspense></ErrorBoundary>;
  }
  // روابط استرجاع السلة المتروكة (/c/:token عرض، /co/:token إكمال) — تُحمّل المتجر على أي دومين
  if(window.location.pathname.startsWith("/store") || /^\/co?\/[A-Za-z0-9]/.test(window.location.pathname) || _isStoreDomain) return <ErrorBoundary><StorefrontGate /></ErrorBoundary>;
  return (
    <ErrorBoundary>
      <OfflineBanner />
      {checkoutRet && (
        <Suspense fallback={null}>
          <CheckoutReturn status={checkoutRet.status} sessionId={checkoutRet.sessionId} onClose={() => setCheckoutRet(null)} />
        </Suspense>
      )}
      <Suspense fallback={<div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc" }}><Spinner /></div>}>
      {!user
        ? <LoginScreen onLogin={handleLogin} />
        : user.role==="superadmin"
          ? <SuperAdminApp user={user} onLogout={handleLogout} />
        : !featuresLoaded
          ? <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc" }}><Spinner /></div>
        : user.role==="company"||user.role==="company_employee"
          ? <CompanyPanel user={user} onLogout={handleLogout} />
          : user.role==="tailor"
            ? (hasFeature("tailor") ? <TailorApp user={user} onLogout={handleLogout} /> : <FeatureDisabled onLogout={handleLogout} />)
            : user.role==="packer"
              ? (hasFeature("packer") ? <PackerApp user={user} onLogout={handleLogout} /> : <FeatureDisabled onLogout={handleLogout} />)
              : user.role==="pos"
                ? (hasFeature("pos") ? <PosApp user={user} onLogout={handleLogout} /> : <FeatureDisabled onLogout={handleLogout} />)
                : <EmployeeApp user={user} onLogout={handleLogout} />
      }
      </Suspense>
    </ErrorBoundary>
  );
}
